import { useEffect, useRef, useState } from 'react';
import type { NoteView, ObjectiveSummaryView } from '@contracts';
import { useDomainApi } from '@ui/shared/domain/DomainApiContext';
import { Plus, ArrowUpRight, Trash2 } from '@ui/shared/icons';
import { PickerOptionRow } from '@ui/desktop/objective-form/FormPrimitives';
import { CardTitle, EmptyCaption, TodayCard, todayInputStyle } from './primitives';

const PRIORITY_CAP = 5;

/**
 * Row C (420px) — Brain dump (R8.1, design/screens/today-desktop-v2.md
 * SS9.2). Capture-only: no checkbox, no date, no objective link rendered
 * anywhere on a row (§2.1). Promote offers exactly two targets (AC-T2); if
 * the Priority target is already at cap, that option disables with the same
 * friendly cap message rather than being silently omitted.
 */
export function BrainDumpCard({
  notes,
  objectives,
  priorityCount,
  onChanged,
}: {
  readonly notes: readonly NoteView[];
  readonly objectives: readonly ObjectiveSummaryView[];
  readonly priorityCount: number;
  readonly onChanged: () => void;
}) {
  const api = useDomainApi();
  const [draft, setDraft] = useState('');

  function addNote() {
    const text = draft.trim();
    if (text.length === 0) return;
    void api.createNote(text).then(() => {
      setDraft('');
      onChanged();
    });
  }

  return (
    <TodayCard label="Brain dump" style={{ width: '420px', flex: 'none' }}>
      <CardTitle>Brain dump</CardTitle>

      <div style={{ marginTop: 'var(--space-3)' }}>
        {notes.length === 0 && <EmptyCaption>Nothing dumped yet — type anything, sort it out later.</EmptyCaption>}
        {notes.map((note) => (
          <BrainDumpRow
            key={note.noteId}
            note={note}
            objectives={objectives}
            priorityCapReached={priorityCount >= PRIORITY_CAP}
            onChanged={onChanged}
          />
        ))}
      </div>

      <div style={{ marginTop: 'var(--space-2)', paddingTop: 'var(--space-2)', borderTop: notes.length > 0 ? '1px solid var(--border-subtle)' : 'none', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <Plus size={16} color="var(--text-muted)" aria-hidden="true" />
        <input
          aria-label="Quick-add a brain-dump note"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') addNote();
          }}
          placeholder="Type anything..."
          style={{ ...todayInputStyle, border: 'none', background: 'transparent', padding: 0 }}
        />
      </div>
    </TodayCard>
  );
}

function BrainDumpRow({
  note,
  objectives,
  priorityCapReached,
  onChanged,
}: {
  readonly note: NoteView;
  readonly objectives: readonly ObjectiveSummaryView[];
  readonly priorityCapReached: boolean;
  readonly onChanged: () => void;
}) {
  const api = useDomainApi();
  const [hover, setHover] = useState(false);
  const [promoteOpen, setPromoteOpen] = useState(false);

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 'var(--space-2)', padding: 'var(--space-2) 0' }}
    >
      <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--type-body-size)', color: 'var(--text-primary)' }}>{note.text}</span>
      <div style={{ display: 'flex', gap: 'var(--space-1)', flex: 'none', opacity: hover ? 1 : 0, transition: 'opacity var(--dur-fast) var(--ease-standard)' }}>
        <button
          type="button"
          aria-label={`Promote ${note.text}`}
          onClick={() => setPromoteOpen(true)}
          style={{ width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}
        >
          <ArrowUpRight size={16} aria-hidden="true" />
        </button>
        <button
          type="button"
          aria-label={`Delete ${note.text}`}
          onClick={() => void api.deleteNote(note.noteId).then(onChanged)}
          style={{ width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}
        >
          <Trash2 size={16} aria-hidden="true" />
        </button>
      </div>
      {promoteOpen && (
        <PromotePopover
          note={note}
          objectives={objectives}
          priorityCapReached={priorityCapReached}
          onClose={() => setPromoteOpen(false)}
          onDone={() => {
            setPromoteOpen(false);
            onChanged();
          }}
        />
      )}
    </div>
  );
}

/** Two-target promote popover (Brain dump only — Ideas' own promote has a
 *  single target, contrast per SS10.1). */
function PromotePopover({
  note,
  objectives,
  priorityCapReached,
  onClose,
  onDone,
}: {
  readonly note: NoteView;
  readonly objectives: readonly ObjectiveSummaryView[];
  readonly priorityCapReached: boolean;
  readonly onClose: () => void;
  readonly onDone: () => void;
}) {
  const api = useDomainApi();
  const [choosingObjective, setChoosingObjective] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handlePointerDown(e: MouseEvent) {
      if (ref.current?.contains(e.target as Node)) return;
      onClose();
    }
    const id = window.setTimeout(() => document.addEventListener('mousedown', handlePointerDown), 0);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [onClose]);

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label={`Promote: ${note.text}`}
      style={{
        position: 'absolute',
        zIndex: 41,
        top: '100%',
        right: 0,
        background: 'var(--surface-overlay)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow-2)',
        border: '1px solid var(--border-subtle)',
        padding: 'var(--space-2)',
        minWidth: '240px',
      }}
    >
      {!choosingObjective ? (
        <>
          <PickerOptionRow label="Turn into an action" selected={false} onSelect={() => setChoosingObjective(true)} />
          <div>
            <PickerOptionRow
              label="Turn into a Priority"
              selected={false}
              onSelect={() => {
                if (priorityCapReached) return;
                void api
                  .promoteNote(note.noteId, { kind: 'priority' })
                  .then(onDone);
              }}
            />
            {priorityCapReached && (
              <p style={{ margin: '0 var(--space-2) var(--space-1)', fontSize: 'var(--type-caption-size)', color: 'var(--text-secondary)' }}>
                Five&apos;s the limit for today — finish or clear one first to add another.
              </p>
            )}
          </div>
        </>
      ) : (
        objectives.map((o) => (
          <PickerOptionRow
            key={o.objectiveId}
            label={o.title}
            selected={false}
            onSelect={() =>
              void api.promoteNote(note.noteId, { kind: 'action', objectiveId: o.objectiveId }).then(onDone)
            }
          />
        ))
      )}
    </div>
  );
}
